import {
  AttendanceVoce,
  AvailableVoce,
  getAvailableVoci,
  saveAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Fuse from "fuse.js";
import {
  addVoceToAttendance,
  attendanceToTempAttendance,
} from "@/screens/app/attendances/attendanceUtils";

type UseAttendanceMobileAddVoceBottomSheetParams = {
  employeeMatricola: number;
  employeeFiliale: number;
  onClose: () => void;
};

export function useAttendanceMobileAddVoceBottomSheet({
  employeeMatricola,
  employeeFiliale,
  onClose,
}: UseAttendanceMobileAddVoceBottomSheetParams) {
  const { lang, selectedMandateId } = useContext(PspContext);
  const {
    setAttendanceMonth,
    setTempAttendanceMonth,
    attendanceMonth,
    selectedMonth,
    selectedYear,
  } = useContext(AttendancesContext);

  const [voceSearchQuery, setVoceSearchQuery] = useState("");
  const [availableVoci, setAvailableVoci] = useState<AvailableVoce[]>();
  const [selectedVoce, setSelectedVoce] = useState<AvailableVoce | null>(null);
  const [quantity, setQuantity] = useState("");
  const [base, setBase] = useState("");

  const attendance = attendanceMonth?.attendances.find(
    (a) =>
      a.employee.matricola === employeeMatricola &&
      a.employee.filiale === employeeFiliale,
  );

  const vociFuse = availableVoci
    ? new Fuse(availableVoci, {
        keys: ["name.textEN", "name.textDE", "name.textIT"],
      })
    : undefined;
  const vociSearchResults =
    vociFuse && voceSearchQuery.length > 0
      ? vociFuse.search(voceSearchQuery).map((result) => result.item)
      : availableVoci;

  useEffect(() => {
    getAvailableVoci().then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }
      setAvailableVoci(data);
    });
  }, [lang]);

  function onAddVoce() {
    if (!selectedVoce) {
      toast(translate("somethingWentWrong", lang));
      throw new Error("Tried to add voce but selectedVoce is undefined.");
    }
    if (!attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      throw new Error("Tried to add voce but attendanceMonth is undefined.");
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }

    const quantityNumber = Number.parseFloat(quantity);
    if (
      Number.isNaN(quantityNumber) &&
      selectedVoce.voceType === "base_by_quantity"
    ) {
      toast(translate("quantityIsNotAValidNumber", lang));
      return;
    }
    const baseNumber = Number.parseFloat(base);
    if (Number.isNaN(baseNumber)) {
      toast(
        translate(
          selectedVoce.voceType === "base_by_quantity"
            ? "baseIsNotAValidNumber"
            : "amountIsNotAValidNumber",
          lang,
        ),
      );
    }

    const newVoce: AttendanceVoce = {
      ...selectedVoce,
      quantity: selectedVoce?.voceType === "amount" ? 1 : quantityNumber,
      base: baseNumber,
    };

    const newAttendance = addVoceToAttendance(
      attendanceMonth,
      employeeMatricola,
      employeeFiliale,
      newVoce,
    );

    saveAttendance({
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
      query: { draft: true },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }

      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
      onCloseWrapper();
    });
  }

  function onCloseWrapper() {
    onClose();
    setTimeout(() => {
      setVoceSearchQuery("");
      setBase("");
      setQuantity("");
      setSelectedVoce(null);
    }, 250);
  }

  return {
    selectedVoce,
    setSelectedVoce,
    lang,
    voceSearchQuery,
    setVoceSearchQuery,
    onAddVoce,
    quantity,
    setQuantity,
    base,
    setBase,
    vociSearchResults,
    onCloseWrapper,
    attendance,
    selectedMonth,
    selectedYear,
  };
}
